<template>
  <div>
    <div
      class="px-4 pb-4 md:px-[50px] lg:px-[100px] md:pb-[50px] lg:pb-[100px] flex flex-col md:gap-[30px] lg:gap-[60px]"
    >
      <div>
        <div class="grid grid-cols-1 gap-[20px]">
          <!-- Search btn -->
          <div class="relative">
            <label for="Search" class="sr-only"> Search </label>
            <input
              type="text"
              id="Search"
              placeholder="Search job title"
              class="w-full h-[60px] rounded-md border-gray-200 py-2.5 px-10 shadow-sm sm:text-sm"
              v-model="searchQuery"
            />
            <span
              class="absolute inset-y-0 right-0 grid w-10 place-content-center"
            >
              <button type="button" class="text-gray-600 hover:text-gray-700">
                <span class="sr-only">Search</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="size-4"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                  />
                </svg>
              </button>
            </span>
          </div>

          <!-- Job Cards Section -->
          <div
            class="w-full overflow-x-auto flex flex-row flex-nowrap space-x-4 pb-4 hide-scrollbar"
            :class="{ 'no-select': isDragging }"
            @mousedown="startDragging"
            @mousemove="onDrag"
            @mouseup="stopDragging"
            @mouseleave="stopDragging"
            ref="scrollContainer"
          >
            <!-- Show "Not Found" message if no results -->
            <div
              v-if="filteredSmartERPData.length === 0"
              class="w-full text-center text-gray-500 mt-4"
            >
              <p class="text-[16px] md:text-[20px] font-medium">
                No jobs found for "{{ searchQuery }}"
              </p>
            </div>

            <!-- Loop through filtered jobs -->
            <div
              v-else
              v-for="(item, index) in filteredSmartERPData"
              :key="index"
              class="flex-shrink-0 w-[280px] md:w-[300px]"
            >
              <!-- Job title card -->
              <div
                @click="getDetail(item.id)"
                :class="{
                  'bg-[#E32027] text-white': selectedJobId === item.id,
                  'bg-white-snow text-gray-800': selectedJobId !== item.id,
                }"
                class="rounded-[10px] p-[20px] flex flex-col gap-[10px] shadow-sm cursor-pointer"
              >
                <h1
                  class="font-bold text-[18px] md:text-[20px] lg:text-[24px] truncate"
                >
                  {{ getLocalized(item, "position_name") }}
                </h1>
                <p class="text-[15px] lg:text-[16px] opacity-70">
                  {{ item.number }}
                  {{ item.number > 1 ? "positions" : "position" }}
                </p>
              </div>
            </div>
          </div>

          <!-- Detailed Card for showing down the list-->
          <div
            v-if="selectItem || smartERPData.length > 0"
            class="basis-full md:basis-3/4 w-full p-[40px] bg-white"
          >
            <div v-if="loading" class="flex flex-col gap-[20px] animate-pulse">
              <div class="flex justify-between items-center">
                <div>
                  <div class="h-[24px] w-[150px] bg-gray-200 rounded-md"></div>
                  <div
                    class="h-[16px] w-[100px] bg-gray-200 rounded-md mt-2"
                  ></div>
                </div>
                <div class="h-[40px] w-[100px] bg-gray-200 rounded-full"></div>
              </div>

              <div class="flex flex-col gap-[10px]">
                <!-- Date and Location Skeleton -->
                <div class="flex flex-col gap-[16px] sm:flex-row">
                  <div class="flex gap-[16px]">
                    <div
                      class="h-[24px] w-[24px] bg-gray-200 rounded-full"
                    ></div>
                    <div
                      class="h-[16px] w-[150px] bg-gray-200 rounded-md"
                    ></div>
                  </div>
                  <div class="flex gap-[16px]">
                    <div
                      class="h-[24px] w-[24px] bg-gray-200 rounded-full"
                    ></div>
                    <div
                      class="h-[16px] w-[150px] bg-gray-200 rounded-md"
                    ></div>
                  </div>
                </div>

                <!-- Working Time Skeleton -->
                <div class="flex gap-[16px]">
                  <div class="h-[26px] w-[26px] bg-gray-200 rounded-full"></div>
                  <div class="h-[16px] w-[100px] bg-gray-200 rounded-md"></div>
                </div>
              </div>

              <hr class="mt-4 mb-4" />

              <!-- Job Description Skeleton -->
              <div class="flex flex-col gap-[30px]">
                <div>
                  <div class="h-[18px] w-[100px] bg-gray-200 rounded-md"></div>
                  <div
                    class="h-[16px] w-full bg-gray-200 rounded-md mt-2"
                  ></div>
                  <div
                    class="h-[16px] w-[80%] bg-gray-200 rounded-md mt-2"
                  ></div>
                </div>

                <!-- Job Requirement Skeleton -->
                <div>
                  <div class="h-[18px] w-[120px] bg-gray-200 rounded-md"></div>
                  <div
                    class="h-[16px] w-full bg-gray-200 rounded-md mt-2"
                  ></div>
                  <div
                    class="h-[16px] w-[70%] bg-gray-200 rounded-md mt-2"
                  ></div>
                </div>
              </div>
            </div>
            <div v-else class="flex flex-col gap-[20px]">
              <div class="flex justify-between">
                <div>
                  <h3
                    class="font-bold text-[18px] md:text-[20px] lg:text-[24px]"
                  >
                    {{
                      getLocalized(
                        selectItem || smartERPData[0],
                        "position_name"
                      )
                    }}
                  </h3>
                  <p class="text-[#222222] text-[15px] lg:text-[16px]">
                    {{ (selectItem || smartERPData[0]).number }} positions
                  </p>
                </div>
                <div>
                  <button
                    class="rounded-[100px] text-[15px] lg:text-[16px] px-[15px] py-[5px] md:px-[20px] md:py-[10px] gap-[10px] text-white bg-tbt-blue"
                    @click="
                      openApplyForm(
                        (selectItem || careersData[0]).position_id,
                        (selectItem || careersData[0]).position_name_en
                      )
                    "
                  >
                    {{ getLocalized1("apply") }}
                  </button>
                </div>
              </div>

              <div class="flex flex-col gap-[10px]">
                <!-- Date and Location -->
                <div class="flex flex-col gap-[16px] sm:flex-row">
                  <div class="flex gap-[16px]">
                    <img class="w-[24px] h-[24px]" :src="calendar" alt="" />
                    <p class="text-[15px] lg:text-[16px]">
                      {{ (selectItem || smartERPData[0]).open_date }} -
                      {{ (selectItem || smartERPData[0]).close_date }}
                    </p>
                  </div>
                  <div class="flex gap-2 items-center">
                    <img class="w-[24px] h-[24px]" :src="location" alt="" />
                    <p class="text-[15px] lg:text-[16px]">
                      {{ (selectItem || smartERPData[0]).location }}
                    </p>
                  </div>
                </div>

                <!-- Working Time -->
                <div class="flex gap-[16px]">
                  <div class="flex gap-[16px]">
                    <img class="w-[26px] h-[26px]" :src="TimeCircle" alt="" />
                    <p class="text-[15px] lg:text-[16px]">
                      {{ getLocalized1("time") }}
                    </p>
                  </div>
                </div>
              </div>

              <hr />

              <!-- Job Description -->
              <div class="flex flex-col gap-[30px]">
                <div>
                  <h3 class="font-bold text-[16px] lg:text-[18px]">
                    {{ getLocalized1("des") }}
                  </h3>
                  <p class="text-[15px] lg:text-[16px]">
                    {{ (selectItem || smartERPData[0]).description }}
                  </p>
                </div>

                <!-- Job Requirement -->
                <div>
                  <h3 class="font-bold text-[16px] lg:text-[18px]">
                    {{ getLocalized1("req") }}
                  </h3>
                  <div>
                    <p class="text-[15px] lg:text-[16px]">
                      {{ (selectItem || smartERPData[0]).requirements }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <apply-form
        v-if="isPopupOpen"
        v-model:position="position"
        v-model:position_name="position_name"
        @close="closeApplyForm"
      ></apply-form>
    </div>
  </div>
</template>

<script>
import calendar from "@/assets/pictures/Calendar.svg";
import work from "@/assets/pictures/Work.svg";
import TimeCircle from "@/assets/pictures/TimeCircle.svg";
import location from "@/assets/pictures/Location.svg";
import axios from "axios";
import { useLocalization } from "@/composable/useLocalization";
import applyForm from "./applyForm.vue";

export default {
  components: { applyForm },
  data() {
    return {
      calendar,
      work,
      TimeCircle,
      location,
      api: process.env.VUE_APP_API_KEY,
      careersData: [],
      selectItem: null,
      searchQuery: "",
      isPopupOpen: false,
      position: "",
      position_name: "",
      selectedJobId: null,
      loading: false,
      isDragging: false,
      startX: 0,
      scrollLeft: 0,
      smartERPData: [],
      translations: {
        title: {
          en: "",
          kh: "",
        },
        time: {
          en: "Full Time",
          kh: "ពេញម៉ោង",
        },
        des: {
          en: "Job Description",
          kh: "ពិពណ៌នាការងារ",
        },
        res: {
          en: "Job Responsibility",
          kh: "ទំនួលខុសត្រូវការងារ",
        },
        req: {
          en: "Job Requirement",
          kh: "តម្រូវការការងារ",
        },
        apply: {
          en: "Apply Now",
          kh: "ដាក់ពាក្យឥឡូវនេះ",
        },
      },

      // Default language is from localStorage or 'en' if not set
      currentLang: localStorage.getItem("language") || "en",
    };
  },
  computed: {
    filteredCareersData() {
      return this.careersData.filter((item) =>
        item.job_title_en.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },
    filteredSmartERPData() {
      return this.smartERPData.filter((item) =>
        item.position_name_en
          .toLowerCase()
          .includes(this.searchQuery.toLowerCase())
      );
    },
    getLocalized() {
      const { getLocalized } = useLocalization();
      return getLocalized;
    },
    getLocalized1() {
      return (key) => {
        return this.translations[key]
          ? this.translations[key][this.currentLang]
          : key;
      };
    },
  },
  mounted() {
    this.fetchFromSmartERP();
  },
  methods: {
    openApplyForm(position, position_name) {
      this.isPopupOpen = true;
      this.position = position;
      this.position_name = position_name;
      document.body.style.overflow = "hidden";
    },
    closeApplyForm() {
      this.isPopupOpen = false;
      this.position = "";
      document.body.style.overflow = "";
    },

    async fetchFromSmartERP() {
      try {
        this.loading = true;
        const response = await axios.get(this.api + "/api/smarterp-api/jobs");
        this.smartERPData = response.data.result;

        // Automatically select the first job if available
        if (this.smartERPData.length > 0) {
          const firstJob = this.smartERPData[0];
          this.selectedJobId = firstJob.id;
          await this.getDetail(firstJob.id);
        }
        this.loading = false;
      } catch (error) {
        console.error("Error fetching SmartERP jobs:", error.message);
      }
    },

    async getDetail(id) {
      try {
        this.loading = true;
        this.selectedJobId = id;
        const response = await axios.get(
          `${this.api}/api/smarterp-api/jobs/${id}`
        );

        // Check if response.data.result exists and is an array with at least one item
        if (response.data.result && response.data.result.length > 0) {
          // Take the first (and likely only) item from the result array
          this.selectItem = response.data.result[0];
        } else {
          console.error("No job details found for the given ID");
          // Optionally, reset selectItem or show an error message
          this.selectItem = null;
        }
        this.loading = false;
      } catch (error) {
        console.error("Error fetching job details:", error.message);
        // Optionally, show a user-friendly error message
        this.selectItem = null;
      }
    },
    startDragging(e) {
      this.isDragging = true;
      this.startX = e.pageX - this.$refs.scrollContainer.offsetLeft;
      this.scrollLeft = this.$refs.scrollContainer.scrollLeft;
    },
    onDrag(e) {
      if (!this.isDragging) return;
      e.preventDefault();
      const x = e.pageX - this.$refs.scrollContainer.offsetLeft;
      const walk = (x - this.startX) * 2; // Adjust scroll speed
      this.$refs.scrollContainer.scrollLeft = this.scrollLeft - walk;
    },
    stopDragging() {
      this.isDragging = false;
    },
  },
};
</script>

<style scoped>
.apply-form-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

.apply-form-popup {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  background-color: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  width: 80%;
  max-width: 600px;
}
/* Custom scrollbar for horizontal scrolling */
.hide-scrollbar {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

.hide-scrollbar::-webkit-scrollbar {
  display: none; /* Chrome, Safari, and Opera */
}

/* Smooth horizontal scrolling with snap */
.scroll-smooth {
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  overscroll-behavior-x: contain;
}

/* Enable smooth scrolling and snapping */
.snap-x {
  scroll-snap-type: x mandatory; /* Horizontal snap */
}
.snap-center {
  scroll-snap-align: center; /* Center align the elements */
}
/* Prevent text selection while dragging */
.no-select {
  user-select: none; /* Disable text selection */
}
</style>
